import {BehaviorSubject} from "rxjs";
import {hookFromSubject, promiseToSubject, ResponseSubjectState} from "./index";
import {CompanyUsersEditDto, Configuration, UserApi, UserCreateDto, UserDto, UserEditDto} from "../generated-api";
import {Auth} from "aws-amplify";
import {
    ROLE_COMPANY_ADMINISTRATOR,
    ROLE_CREATOR,
    ROLE_READER,
    ROLE_READER_DOWNLOADER,
    ROLE_TRACERT_ADMINISTRATOR
} from "../constants/user";
import {Redirect} from "../components/Redirect";

export const authenticatedUserSubject = new BehaviorSubject<any>(null);
export const useAuthenticatedUser = hookFromSubject(authenticatedUserSubject);

export const checkState = (): void => {
    authenticatedUserSubject.next(null);
    Auth.currentAuthenticatedUser().then(user => {
        authenticatedUserSubject.next(user);
        getUserMe();
    }).catch(console.error);
};

export const getCustomHeaders = () :{Authorization?: string, UserCompanyType: string} => {
    const user = authenticatedUserSubject.getValue();
    if (!user) {
        return {UserCompanyType: "CertTrace"};
    }
    return {
        Authorization: 'Bearer ' + user.signInUserSession.accessToken.jwtToken,
        UserCompanyType: "CertTrace"
    }
}

export const permissions = {
    certificateDocuments: [ROLE_READER, ROLE_READER_DOWNLOADER, ROLE_CREATOR, ROLE_COMPANY_ADMINISTRATOR, ROLE_TRACERT_ADMINISTRATOR],
    downloadCertificateDocuments: [ROLE_READER_DOWNLOADER, ROLE_CREATOR, ROLE_COMPANY_ADMINISTRATOR, ROLE_TRACERT_ADMINISTRATOR],
    certificatesSearch: [ROLE_CREATOR, ROLE_TRACERT_ADMINISTRATOR],
    manageCertificates: [ROLE_CREATOR, ROLE_TRACERT_ADMINISTRATOR]
};

export const hasAnyRole = (roles: string[] = []): boolean => {
    let success = false;
    const userRoles = userRolesMeSubject.getValue();
    roles.forEach(role => {
        if (userRoles.includes(role)) {
            success = true;
        }
    });
    return !!roles.length && success;
};

export const isAuthenticated = (): boolean => !!authenticatedUserSubject.getValue();
export const isTraCertAdmin = (): boolean => userRolesMeSubject.getValue().includes(ROLE_TRACERT_ADMINISTRATOR);
export const isCompanyAdmin = (): boolean => userRolesMeSubject.getValue().includes(ROLE_COMPANY_ADMINISTRATOR);
export const isAdmin = (): boolean => isCompanyAdmin() || isTraCertAdmin();

export const forAuthenticated = (component: any, props: any) => isAuthenticated() ? component(props) : Redirect({to: '/dashboard'});
export const forNotAuthenticated = (component: any, props: any) => !isAuthenticated() ? component(props) : Redirect({to: '/dashboard'});
export const forTraCertAdmin = (component: any, props: any) => isTraCertAdmin() ? component(props) : Redirect({to: '/dashboard'});
export const forCompanyAdmin = (component: any, props: any) => isCompanyAdmin() ? component(props) : Redirect({to: '/dashboard'});
export const forAdmin = (component: any, props: any) => isAdmin() ? component(props) : Redirect({to: '/dashboard'});

export const forRoles = (component: any, props: any, roles: string[] = []) => {
    if (!hasAnyRole(roles)) {
        Redirect({to: '/dashboard'});
    } else {
        return component(props);
    }
};


const {REACT_APP_API_DOMAIN} = process.env;
const userApi = new UserApi(new Configuration({basePath: REACT_APP_API_DOMAIN,  getHeaders: getCustomHeaders}));

export const userSubject = new BehaviorSubject(new ResponseSubjectState());
export const useUser = hookFromSubject(userSubject);
export const getUser = () =>
    promiseToSubject(userApi.apiUserGet(), userSubject);

export const userCreateSubject = new BehaviorSubject(new ResponseSubjectState());
export const useUserCreate = hookFromSubject(userCreateSubject);
export const createUser = (body?: UserCreateDto) =>
    promiseToSubject(userApi.apiUserPost(body), userCreateSubject);

export const userIdSubject = new BehaviorSubject(new ResponseSubjectState());
export const useUserId = hookFromSubject(userIdSubject);
export const getUserId = (id: string, suppressErrors?: boolean) =>
    promiseToSubject(userApi.apiUserIdGet({id}), userIdSubject, {suppressErrors});

export const updateUser = (userId: string, body?: UserEditDto) =>
    promiseToSubject(userApi.apiUserUserIdPut({userId}, body), userIdSubject);

export const userMeSubject = new BehaviorSubject(new ResponseSubjectState());
export const useUserMe = hookFromSubject(userMeSubject);
export const getUserMe = () =>
    promiseToSubject(userApi.apiUserMeGet(), userMeSubject);

export const userSystemRolesSubject = new BehaviorSubject(new ResponseSubjectState());
export const useUserSystemRoles = hookFromSubject(userSystemRolesSubject);
export const getUserSystemRoles = () =>
    promiseToSubject(userApi.apiUserCreateRolesGet(), userSystemRolesSubject);

export const userStatusesSubject = new BehaviorSubject(new ResponseSubjectState());
export const useUserStatuses = hookFromSubject(userStatusesSubject);
export const getUserStatuses = () =>
    promiseToSubject(userApi.apiUserStatusesGet(), userStatusesSubject);

export const updateCustomerUsersSubject = new BehaviorSubject(new ResponseSubjectState());
export const useUpdateCustomerUsers = hookFromSubject(updateCustomerUsersSubject);
export const updateCustomerUsers = (userId: string, body: CompanyUsersEditDto) =>
    promiseToSubject(userApi.apiUserUserIdCompanyUsersPut({userId}, body), updateCustomerUsersSubject);

export const userRolesMeSubject = new BehaviorSubject<any[]>([]);
userMeSubject.subscribe((state) => {
    if (!state.loading && state.data) {
        const user: UserDto = state.data;
        userRolesMeSubject.next((user.userCompaniesRoles.find(x=>x.companyType === 1)?.roles || []).map(item => item.role));
    }
});
export const useRolesMe = hookFromSubject(userRolesMeSubject);
